// The single page behind pressuretest.sagentivum.com.
//
// Funnel sequence (top → bottom = funnel left → right):
//   1. Top nav (brand + trail)
//   2. Page title + intro grid
//   3. Intelligence Tier model header (green hero)
//   4. WhereDoIFit (free)
//   5. Department cards - tier bands + system functions + measurement (free)
//   6. EmailGate - collects org chart + email (Tier 1 unlock)
//   7. DiagnosisStream - Opus 4.7 streamed read (after unlock)
//   8. TenModelGrid - locked grid with 3 lenses identified (after diagnosis)
//   9. MeetingGate - Calendly popup (after lens grid)

import { useEffect, useRef, useState } from 'react';
import DepartmentCard from '../components/DepartmentCard';
import WhereDoIFit, { type FitState } from '../components/WhereDoIFit';
import EmailGate, { type EmailGateUnlock } from '../components/EmailGate';
import DiagnosisStream from '../components/DiagnosisStream';
import ElevenModelGrid from '../components/ElevenModelGrid';
import MeetingGate from '../components/MeetingGate';
import FloatingBookCTA from '../components/FloatingBookCTA';
import { intelligenceTier } from '../data/intelligenceTier';
import { renderEmphasis } from '../lib/text';
import { track } from '../lib/mixpanel';
import type { SelectedLens } from '../lib/api';
import PublicSummary from '../components/PublicSummary';


export default function PressureTest() {
  const model = intelligenceTier;

  const [latestFit, setLatestFit] = useState<FitState | null>(null);
  const [unlocked, setUnlocked] = useState<EmailGateUnlock | null>(null);
  const [diagnosis, setDiagnosis] = useState<string | null>(null);
  const [selectedLenses, setSelectedLenses] = useState<SelectedLens[] | null>(null);
  const [introOpen, setIntroOpen] = useState(false);

  const diagnosisRef = useRef<HTMLDivElement | null>(null);
  const gridRef = useRef<HTMLDivElement | null>(null);
  const meetingRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    track('landing_viewed', { // event 1
      referrer: document.referrer || null,
      utm_source: params.get('utm_source'),
      utm_medium: params.get('utm_medium'),
      utm_campaign: params.get('utm_campaign'),
    });
  }, []);

  useEffect(() => {
    if (unlocked) diagnosisRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [unlocked]);

  useEffect(() => {
    if (diagnosis) gridRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [diagnosis]);

  useEffect(() => {
    if (selectedLenses) meetingRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [selectedLenses]);

  const tierDepts = model.departments.slice(0, 3);
  const systemDepts = model.departments.slice(3, 6);
  const measureDept = model.departments[6];

  return (
    <div className="font-sans text-ink-soft selection:bg-green-700 selection:text-paper">
      {/* TOP NAV */}
      <header className="sticky top-0 z-10 bg-green-700">
        <div className="max-w-[1180px] mx-auto px-6 h-[65px] flex items-center justify-between">
          <a href="https://www.sagentivum.com/" className="flex items-center gap-2.5">
            <img src="/logo-white.png" alt="Sagentivum" className="w-[26px] h-[26px]" />
            <span className="font-display font-bold text-[17px] text-paper tracking-[-0.01em]">
              Sagentivum
            </span>
          </a>
          <div className="text-[13px] text-green-200 tracking-[0.02em]">
            <strong className="text-paper font-medium">Pressure Test</strong>
            <span className="hidden sm:inline">&nbsp;&nbsp;·&nbsp;&nbsp;Intelligence Tier</span>
          </div>
        </div>
      </header>

      {/* PAGE TITLE + INTRO */}
      <section className="relative overflow-hidden bg-paper border-b border-border pt-10 pb-12 md:pt-24 md:pb-16">
        <img
          src="/logo-green.png"
          alt=""
          className="absolute -top-10 -right-20 w-[520px] aspect-square opacity-[0.05] pointer-events-none"
        />
        <div className="relative max-w-[1180px] mx-auto px-6">
          <h1 className="font-display text-[clamp(32px,5vw,52px)] font-bold tracking-[-0.025em] leading-[1.05] text-ink max-w-[880px] mb-6 md:mb-8">
            Your organisation is already being redesigned.
            <br />
            <em className="not-italic text-green-700">You just haven't decided how.</em>
          </h1>

          {/* BENEFIT + STEP RAIL - answers "what do I get / how long" before any scroll */}
          <p className="text-[17px] md:text-[19px] leading-[1.55] text-ink font-medium max-w-[720px] mb-5 md:mb-6">
            Get a board-level diagnosis of your org structure, written for you in
            under two minutes.
          </p>

          <ol className="list-none p-0 mb-7 md:mb-9 flex flex-col md:flex-row md:items-center gap-2.5 md:gap-0 max-w-[880px]">
            {[
              'Place your role',
              'Describe your organisation',
              'Read your diagnosis',
            ].map((step, i) => (
              <li
                key={step}
                className="flex items-center gap-2.5 md:gap-3 md:pr-6 md:mr-6 md:border-r md:border-border md:last:border-r-0 md:last:pr-0 md:last:mr-0"
              >
                <span className="shrink-0 w-6 h-6 rounded-full bg-green-700 text-paper text-[12px] font-semibold flex items-center justify-center tabular-nums">
                  {i + 1}
                </span>
                <span className="text-[14px] md:text-[14.5px] text-ink-soft leading-tight">
                  {step}
                </span>
              </li>
            ))}
          </ol>

          <div className="text-[12px] font-medium tracking-[0.04em] uppercase text-stone-500 mb-7 md:mb-8">
            Free · No mailing list
          </div>

          <button
            type="button"
            onClick={() => {
              track('hero_cta_clicked', { variant: 'mobile_where_do_i_fit' });
              document
                .getElementById('where-do-i-fit')
                ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }}
            className="md:hidden mb-8 w-full bg-green-700 text-paper px-6 py-3.5 rounded-md text-[15px] font-medium flex items-center justify-center gap-2 hover:bg-green-800 active:bg-green-800 transition-colors"
          >
            See where you fit
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M6 9l6 6 6-6" />
            </svg>
          </button>

          {/* Mobile only: the first-principles argument is long, so it sits behind a
              disclosure to keep headline -> benefit -> steps -> CTA above the fold.
              Desktop renders it inline as before. */}
          <button
            type="button"
            onClick={() => {
              const next = !introOpen;
              setIntroOpen(next);
              if (next) track('hero_intro_expanded', { variant: 'mobile_disclosure' });
            }}
            aria-expanded={introOpen}
            aria-controls="hero-intro"
            className="md:hidden mb-4 flex items-center gap-2 text-[14px] font-medium text-green-700"
          >
            Why the lines were drawn where they were
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="15"
              height="15"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
              className={`transition-transform ${introOpen ? 'rotate-180' : ''}`}
            >
              <path d="M6 9l6 6 6-6" />
            </svg>
          </button>

          <div
            id="hero-intro"
            className={`${introOpen ? 'grid' : 'hidden'} md:grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-12 max-w-[880px]`}
          >
            <div className="space-y-4 text-[15px] leading-[1.7] text-ink-mute">
              <p>
                Org structures were built around a simple constraint: the limits of what a human mind can hold, process, and act on simultaneously. That constraint determined where the lines between functions were drawn.
              </p>
              <p>
                AI doesn't work faster within those lines. It dissolves the reason the lines existed in the first place.
              </p>
            </div>
            <div className="space-y-4 text-[15px] leading-[1.7] text-ink-mute">
              <p>
                So if the boundaries were drawn around human cognitive limits - and those limits no longer apply - what should the structure actually look like?
              </p>
              <p>
                We've derived ten models from first principles. Each starts from a different question about what an organisation fundamentally is. This is one of them.
              </p>
            </div>
          </div>
          <div className="mt-6 md:mt-8">
            <PublicSummary />
          </div>
        </div>
      </section>

      {/* INTELLIGENCE TIER MODEL HEADER */}
      <section className="relative overflow-hidden bg-green-700 text-paper py-16">
        <img
          src="/logo-white.png"
          alt=""
          className="absolute -bottom-32 -left-20 w-[480px] aspect-square opacity-[0.06] pointer-events-none"
        />
        <div className="relative max-w-[1180px] mx-auto px-6 grid grid-cols-1 md:grid-cols-[1fr_auto] gap-12 items-end">
          <div>
            <div className="text-[12px] font-semibold tracking-mega uppercase text-green-200 mb-3">
              Model 04 · Applied to your organisation
            </div>
            <h2 className="font-display text-[clamp(40px,5vw,56px)] font-bold tracking-[-0.025em] leading-none text-paper mb-4">
              {model.name}
            </h2>
            <p className="text-[15px] leading-[1.7] text-green-100 max-w-[640px]">
              {renderEmphasis(model.principle)}
            </p>
          </div>
          <div className="md:text-right md:border-l md:border-[rgba(245,241,233,0.18)] md:pl-8">
            <div className="font-display text-[72px] font-bold leading-none text-paper tracking-[-0.03em]">
              {model.deptCount}
            </div>
            <div className="text-[12px] text-green-200 mt-2 leading-[1.5] uppercase tracking-[0.04em] font-medium">
              Departments
              <span className="block text-clay-tint font-medium normal-case tracking-normal">
                {model.newCount} entirely new
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* WHERE DO I FIT */}
      <WhereDoIFit onResult={setLatestFit} />

      {/* DEPARTMENT CARDS */}
      <section className="bg-paper border-b border-border py-16">
        <div className="max-w-[1180px] mx-auto px-6">
          <h2 className="font-display text-[28px] font-semibold tracking-[-0.015em] text-ink mt-3 mb-2">
            The Model
          </h2>
          <div className="text-[11px] font-semibold tracking-mega uppercase text-green-700 mb-4 flex items-center gap-3">
            <span>Operating layers</span>
            <span className="flex-1 h-px bg-border" />
          </div>

          {/* Tier bands */}
          <div className="space-y-3 mb-12">
            {tierDepts.map((dept) => (
              <DepartmentCard key={dept.name} dept={dept} />
            ))}
          </div>

          {/* System Functions */}
          <div className="mb-12">
            <div className="text-[11px] font-semibold tracking-mega uppercase text-green-700 mb-4 flex items-center gap-3">
              <span>System functions · New layer</span>
              <span className="flex-1 h-px bg-border" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-start">
              {systemDepts.map((dept) => (
                <DepartmentCard key={dept.name} dept={dept} />
              ))}
            </div>
          </div>

          {/* Measurement */}
          <div>
            <div className="text-[11px] font-semibold tracking-mega uppercase text-stone-500 mb-4 flex items-center gap-3">
              <span>Measurement</span>
              <span className="flex-1 h-px bg-border" />
            </div>
            <DepartmentCard dept={measureDept} />
          </div>
        </div>
      </section>

      {/* TIER 1 - EMAIL GATE */}
      {!unlocked && (
        <div id="email-gate">
          <EmailGate onUnlocked={setUnlocked} fitState={latestFit} />
        </div>
      )}

      {/* TIER 1 - DIAGNOSIS STREAM */}
      {unlocked && (
        <div ref={diagnosisRef} id="diagnosis-section">
          <DiagnosisStream orgChart={unlocked.orgChart} hasFitResult={!!latestFit} onComplete={setDiagnosis} />
        </div>
      )}

      {/* TIER 1 - TEN-MODEL GRID (also fires lead notification) */}
      {unlocked && diagnosis && (
        <div ref={gridRef}>
          <ElevenModelGrid
            orgChart={unlocked.orgChart}
            diagnosis={diagnosis}
            jobTitle={latestFit?.jobTitle}
            fitResult={latestFit?.result}
            onSelected={setSelectedLenses}
          />
        </div>
      )}

      {/* TIER 2 - MEETING GATE */}
      {selectedLenses && (
        <div ref={meetingRef}>
          <MeetingGate selectedLenses={selectedLenses} />
        </div>
      )}

      {/* FLOATING CTA - appears once diagnosis is done, stays until page end */}
      <FloatingBookCTA show={!!diagnosis} />
    </div>
  );
}
